import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Divider,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { CreateDonationRequest, CreateDonorRequest, PaymentMethod, GeolocationPosition } from '@/types';
import { DonorForm } from './DonorForm';
import { DonationDetailsForm } from './DonationDetailsForm';
import { ReceiptUploadForm } from './ReceiptUploadForm';
import { LocationForm } from './LocationForm';
import { apiService } from '@/services/api';
import { offlineStorageService } from '@/services/offlineStorage';
import { notificationService } from '@/services/notificationService';
import { appStateService } from '@/services/appStateService';

const steps = [
  { label: 'Donatore', icon: <PersonAddIcon /> },
  { label: 'Donazione', icon: <PaymentIcon /> },
  { label: 'Ricevuta', icon: <ReceiptIcon /> },
  { label: 'Posizione', icon: <LocationIcon /> },
];

interface DonationFormProps {
  onSuccess: () => void;
}

export const DonationForm: React.FC<DonationFormProps> = ({ onSuccess }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<{
    donor: CreateDonorRequest;
    donation: Partial<CreateDonationRequest>;
    location?: GeolocationPosition;
    receipt?: File;
  }>({
    donor: {
      email: '',
      firstName: '',
      lastName: '',
      phone: '',
      address: '',
      city: '',
      postalCode: '',
      country: 'IT',
      gdprConsent: false,
      marketingConsent: false,
    },
    donation: {
      amount: 0,
      paymentMethod: 'CASH' as PaymentMethod,
      notes: '',
    },
  });

  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
    
    // Scroll automatico in alto quando si passa al passo successivo
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100); // Piccolo delay per permettere al DOM di aggiornarsi
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    
    // Scroll automatico in alto quando si torna al passo precedente
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100); // Piccolo delay per permettere al DOM di aggiornarsi
  };

  const handleDonorChange = (donorData: Partial<CreateDonorRequest>) => {
    setFormData(prev => ({
      ...prev,
      donor: { ...prev.donor, ...donorData }
    }));
  };

  const handleDonationChange = (donationData: Partial<CreateDonationRequest>) => {
    console.log('DonationForm received:', { 
      donationData, 
      amount: donationData.amount, 
      amountType: typeof donationData.amount 
    });
    
    // Assicurati che l'importo sia sempre un numero
    const cleanDonationData = {
      ...donationData,
      amount: typeof donationData.amount === 'string' ? parseFloat(donationData.amount) : donationData.amount
    };
    
    console.log('Cleaned donation data:', { 
      cleanDonationData, 
      amount: cleanDonationData.amount, 
      amountType: typeof cleanDonationData.amount 
    });
    
    setFormData(prev => ({
      ...prev,
      donation: { ...prev.donation, ...cleanDonationData }
    }));
  };

  const handleReceiptChange = (receipt: File | undefined) => {
    setFormData(prev => ({
      ...prev,
      receipt
    }));
  };

  const handleLocationChange = (location: GeolocationPosition | undefined) => {
    setFormData(prev => ({
      ...prev,
      location
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      // Validazione posizione obbligatoria
      if (!formData.location) {
        throw new Error('La posizione è obbligatoria per completare la donazione');
      }

      // Validazione importo
      if (!formData.donation.amount || formData.donation.amount <= 0) {
        throw new Error('L\'importo della donazione deve essere maggiore di zero');
      }

      let donorId: string;

      // Crea o trova donatore
      if (isOnline) {
        try {
          // Prova a creare donatore online
          const createdDonor = await apiService.createDonor(formData.donor);
          donorId = createdDonor.id;
        } catch (error: any) {
          // Mostra l'errore specifico invece di salvarlo offline silenziosamente
          const errorMessage = error.response?.data?.message || error.message || 'Errore durante la creazione del donatore';
          notificationService.error('Errore creazione donatore', errorMessage);
          throw error; // Rilancia l'errore per bloccare il processo
        }
      } else {
        // Salva donatore offline
        const donorIdTemp = `donor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        await offlineStorageService.saveOfflineDonor({
          ...formData.donor,
          id: donorIdTemp
        });
        donorId = donorIdTemp;
      }

      // Ottieni ID del collector (utente corrente)
      const currentUser = appStateService.getState().user;
      if (!currentUser) {
        throw new Error('Utente non autenticato');
      }

      // Crea donazione
      const amount = typeof formData.donation.amount === 'string' ? parseFloat(formData.donation.amount) : (formData.donation.amount || 0);
      
      const donationData: CreateDonationRequest = {
        amount,
        paymentMethod: formData.donation.paymentMethod || 'CASH',
        notes: formData.donation.notes || '',
        donorId,
        collectorId: currentUser.id,
        latitude: formData.location?.latitude,
        longitude: formData.location?.longitude,
        address: formData.location ? 
          `${formData.location.latitude.toFixed(6)}, ${formData.location.longitude.toFixed(6)}` : 
          undefined,
      };

      // Debug: verifica che l'importo sia un numero
      console.log('Donation data being sent:', {
        ...donationData,
        amountType: typeof donationData.amount,
        amountValue: donationData.amount,
        originalFormData: formData.donation
      });

      if (isOnline) {
        try {
          // Prova a creare donazione online
          const createdDonation = await apiService.createDonation(donationData);
          
          // Se c'è una foto del pagamento, caricala
          if (formData.receipt) {
            try {
              // Carica la foto del documento
              const photoUploadResult = await apiService.uploadDocumentPhoto({
                file: formData.receipt,
                donationId: createdDonation.id,
              });

              // Aggiorna la donazione con i metadati della foto
              await apiService.updateDonation(createdDonation.id, {
                documentPhotoS3Key: photoUploadResult.s3Key,
                documentPhotoS3Url: photoUploadResult.s3Url,
                documentPhotoOriginalName: photoUploadResult.originalName,
                documentPhotoMimeType: photoUploadResult.mimeType,
                documentPhotoFileSize: photoUploadResult.fileSize,
              });
            } catch (uploadError) {
              console.warn('Errore durante l\'upload della foto pagamento:', uploadError);
              // Non bloccare il processo se l'upload della foto fallisce
              notificationService.warning(
                'Foto Pagamento',
                'La donazione è stata creata ma la foto non è stata caricata. Puoi riprovare più tardi.'
              );
            }
          }

          notificationService.donationCreated(formData.donation.amount || 0);
        } catch (error) {
          // Se fallisce, salva offline
          await offlineStorageService.saveOfflineDonation(donationData);
          
          if (formData.receipt) {
            await offlineStorageService.saveOfflineReceipt(
              `donation_${Date.now()}`,
              formData.receipt
            );
          }

          notificationService.donationCreated(formData.donation.amount || 0);
        }
      } else {
        // Salva donazione offline
        const localId = `donation_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        await offlineStorageService.saveOfflineDonation(donationData);
        
        if (formData.receipt) {
          await offlineStorageService.saveOfflineReceipt(
            localId,
            formData.receipt
          );
        }

        notificationService.donationCreated(formData.donation.amount || 0);
      }

      // Aggiorna conteggio sincronizzazioni pendenti
      await appStateService.refreshPendingSyncCount();

      onSuccess();
    } catch (error) {
      console.error('Error submitting donation:', error);
      notificationService.error(
        'Errore',
        'Impossibile salvare la donazione. Riprova più tardi.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };


  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <DonorForm
            data={formData.donor}
            onChange={handleDonorChange}
            onNext={handleNext}
            disabled={isSubmitting}
          />
        );
      case 1:
        return (
          <DonationDetailsForm
            data={{
              amount: formData.donation.amount || 0,
              paymentMethod: formData.donation.paymentMethod || 'CASH',
              notes: formData.donation.notes,
              donorId: '',
              collectorId: '',
            }}
            onChange={handleDonationChange}
            onNext={handleNext}
            onBack={handleBack}
            disabled={isSubmitting}
          />
        );
      case 2:
        return (
          <ReceiptUploadForm
            file={formData.receipt}
            onChange={handleReceiptChange}
            onNext={handleNext}
            onBack={handleBack}
            disabled={isSubmitting}
          />
        );
      case 3:
        return (
          <LocationForm
            location={formData.location}
            onChange={handleLocationChange}
            onSubmit={handleSubmit}
            onBack={handleBack}
            disabled={isSubmitting}
            isSubmitting={isSubmitting}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
      <Card>
        <CardContent>
          <Typography variant="h5" component="h1" gutterBottom align="center">
            Nuova Donazione
          </Typography>

          {!isOnline && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Modalità offline: la donazione verrà sincronizzata quando tornerai online.
            </Alert>
          )}

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((step) => (
              <Step key={step.label}>
                <StepLabel icon={step.icon}>
                  {step.label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          <Divider sx={{ mb: 3 }} />

          {renderStepContent(activeStep)}
        </CardContent>
      </Card>
    </Box>
  );
};
